import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PE_IMG, PEHead, GoldRule } from "./bits";
import Reveal from "../Reveal";
import { api, fmtMoney } from "../../lib/peApi";

const CHAPTERS = [
  { id: "prismhouse", n: "01", title: "The Prism House", sub: "Hair", img: PE_IMG.prismhouse, imgAlt: "Polished textured hair, styled with intention", note: "healthy, polished, intentional", banner: "Every hair service includes shampooing, standard preparation and standard customization." },
  { id: "facials", n: "02", title: "The Essence Facials", sub: "Skin", img: PE_IMG.facials, imgAlt: "Glowing skin after an Essence facial", note: "the facial collection" },
  { id: "maintenance", n: "03", title: "Prism Maintenance", sub: "Waxing + Brow Care", note: "polished and precise" },
  { id: "studio", n: "04", title: "The Prism Studio", sub: "Makeup", img: PE_IMG.studio, imgAlt: "Editorial makeup artistry in warm neutral tones", note: "another dimension of expression" },
  { id: "education", n: "05", title: "Beauty Education", sub: "Learn", img: PE_IMG.texture, imgAlt: "Champagne silk — the texture of the house", note: "leave feeling capable" },
];

function PriceTag({ s }) {
  if (s.contact_only) return <p className="font-pe text-lg text-pe-olive italic">Contact for More Information</p>;
  if (s.price) return (
    <>
      <p className="font-pe text-lg md:text-xl text-pe-brass">{s.starting ? "from " : ""}{fmtMoney(s.price)}</p>
      <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-pe-muted mt-1">{s.duration_label}</p>
    </>
  );
  return (
    <>
      <p className="font-pe text-lg text-pe-olive italic">consult</p>
      <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-pe-muted mt-1">{s.duration_label}</p>
    </>
  );
}

export default function PEMenu({ onBook }) {
  const [services, setServices] = useState([]);
  const [policies, setPolicies] = useState(null);

  useEffect(() => {
    api("/services").then((d) => setServices(d.services)).catch(() => {});
    api("/policies").then(setPolicies).catch(() => {});
  }, []);

  return (
    <section id="pe-menu" data-testid="pe-menu" className="bg-pe-ivory py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <PEHead overline="The Beauty Menu" title={<>Every <span className="italic">dimension.</span></>} />
        <Reveal>
          <div className="text-center -mt-8 mb-20 max-w-2xl mx-auto">
            <p className="font-sans font-light text-sm text-pe-muted leading-relaxed">
              A <strong className="text-pe-forest font-medium">$25 deposit</strong> secures every appointment and is applied to your service.
              First visit? <strong className="text-pe-forest font-medium">20% off</strong> your first service. Your fifth completed service earns
              <strong className="text-pe-forest font-medium"> 50% off</strong>, and referrals earn <strong className="text-pe-forest font-medium">15% off</strong> — one reward at a time, unless the house says otherwise.
            </p>
            {policies && (
              <p className="font-sans font-light text-[12px] text-pe-muted leading-relaxed mt-4" data-testid="pe-menu-policy">
                {policies.cancellation}{" "}
                <Link to="/prismatic-essence/guest" className="text-pe-brass underline underline-offset-4">Booked as a guest?</Link>
              </p>
            )}
          </div>
        </Reveal>

        <div className="space-y-24 md:space-y-32">
          {CHAPTERS.map((ch) => (
            <div key={ch.id} data-testid={`pe-chapter-${ch.id}`} className="grid lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-4">
                <Reveal>
                  <p className="font-pe text-6xl md:text-7xl text-pe-gold/80 leading-none mb-3">{ch.n}.</p>
                  <h3 className="font-pe text-2xl md:text-3xl text-pe-forest tracking-wide mb-1.5">{ch.title}</h3>
                  <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-pe-olive mb-2">{ch.sub}</p>
                  <p className="font-pe italic text-lg text-pe-olive mb-6">{ch.note}</p>
                  <GoldRule className="w-20 mb-8" />
                  {ch.banner && <p className="font-sans text-[12px] text-pe-muted leading-relaxed mb-6 max-w-xs">{ch.banner}</p>}
                  {ch.img && (
                    <div className="relative max-w-xs">
                      <div className="absolute -inset-2 border border-pe-gold/40 rounded-sm translate-x-3 translate-y-3" aria-hidden="true" />
                      <img src={ch.img} alt={ch.imgAlt} className="relative w-full aspect-[4/5] object-cover rounded-sm" loading="lazy" />
                    </div>
                  )}
                </Reveal>
              </div>

              <div className="lg:col-span-8">
                <Reveal delay={0.1}>
                  <ul>
                    {services.filter((s) => s.chapter === ch.id).map((s) => (
                      <li
                        key={s.id}
                        data-testid={`pe-service-${s.id}`}
                        className="group flex items-start justify-between gap-6 py-5 border-b border-pe-sand/60 first:pt-0"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <p className="font-pe text-xl md:text-2xl text-pe-forest group-hover:text-pe-olive transition-colors">{s.name}</p>
                            {s.id === "braiding" && <span className="rounded-full border border-pe-gold/60 text-pe-brass px-2.5 py-0.5 font-sans text-[9px] tracking-[0.15em] uppercase">Starting price</span>}
                          </div>
                          <p className="font-sans font-light text-sm text-pe-muted mt-1">{s.includes.join(" ")}</p>
                          {s.notes.map((n) => <p key={n} className="font-sans font-light text-[12px] text-pe-muted/80 italic mt-0.5">{n}</p>)}
                        </div>
                        <div className="text-right shrink-0 flex flex-col items-end gap-2.5">
                          <PriceTag s={s} />
                          {!s.contact_only && (
                            <button
                              data-testid={`pe-book-${s.id}`}
                              onClick={() => onBook(s.id)}
                              className="rounded-full border border-pe-brass/60 text-pe-forest px-4 py-1.5 font-sans text-[9px] tracking-[0.2em] uppercase hover:bg-pe-forest hover:text-pe-ivory transition-colors"
                            >
                              Book · $25 dep
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
